import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import CandidateCard from './CandidateCard';

const SearchResults = ({ 
  candidates, 
  totalCount, 
  isLoading, 
  sortBy, 
  onSortChange, 
  onUnlockCandidate, 
  onAddToShortlist,
  shortlistedCandidates 
}) => {
  const sortOptions = [
    { value: 'match', label: 'Best Match' },
    { value: 'experience', label: 'Experience Level' },
    { value: 'recent', label: 'Recently Active' },
    { value: 'salary', label: 'Salary Range' },
    { value: 'availability', label: 'Availability' }
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-muted rounded w-32 animate-pulse" />
          <div className="h-10 bg-muted rounded w-48 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {[...Array(6)]?.map((_, index) => (
            <div key={index} className="bg-card border border-border rounded-xl p-6 animate-pulse">
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-12 w-12 bg-muted rounded-full" />
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-32" />
                  <div className="h-3 bg-muted rounded w-24" />
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-3 bg-muted rounded w-full" />
                <div className="h-3 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (candidates?.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="h-24 w-24 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Search" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No candidates found</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          Try adjusting your search filters or expanding your criteria to find more candidates.
        </p>
        <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Lightbulb" size={16} />
          <span>Tip: Use broader skill categories or increase location radius</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-foreground">
            {totalCount?.toLocaleString('no-NO')} candidates found
          </h2>
          {totalCount > 0 && (
            <div className="flex items-center space-x-1 text-sm text-muted-foreground">
              <Icon name="Filter" size={16} />
              <span>Filtered results</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Icon name="BarChart3" size={16} className="text-muted-foreground" />
            <Select
              placeholder="Sort by"
              options={sortOptions}
              value={sortBy}
              onChange={onSortChange}
              className="w-48"
            />
          </div>
        </div>
      </div>
      {/* Results Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {candidates?.map((candidate) => (
          <CandidateCard
            key={candidate?.id}
            candidate={candidate}
            onUnlock={onUnlockCandidate}
            onAddToShortlist={onAddToShortlist}
            isInShortlist={shortlistedCandidates?.some(c => c?.id === candidate?.id)}
          />
        ))}
      </div>
      {/* Load More */}
      {candidates?.length < totalCount && (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">
            Showing {candidates?.length} of {totalCount?.toLocaleString('no-NO')} candidates
          </p>
          <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
            <Icon name="ArrowDown" size={16} />
            <span>Scroll down to load more results</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResults;